import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { environment } from 'src/environments/environment';
import { UserAuthService } from '../UserAuth.service';
import { CureSheetProvenance } from '../models/cure-sheet-ref';

/** One entry of the audience list — VIEWER-CONTRACTS.md §2b. */
export interface CureSheetListItem {
  id: string;
  /** The EXACT pinned version, never the sheet's newest. */
  version: number;
  /** THE DISPLAY NAME. Without it every sheet is a GUID. */
  brandName: string;
  description?: string;
  /** The document, NOT the product — never label a sheet with this. */
  fileName?: string;
  updatedAt?: string;
  meta?: unknown;
}

export interface CureSheetAudience {
  sheets: CureSheetListItem[];
  defaultSheetId: string | null;
  lockedFields: string[];
}

/** A PDF plus the standing that must travel with it. */
export interface CureSheetPdf {
  blob: Blob;
  /**
   * From `X-Vericure-Cure-Sheet-Standing`.
   *
   * The server sends it because a PDF opened in a viewer has none of the page
   * chrome that would say the document was withdrawn.
   */
  standingHeader: string | null;
}

/**
 * Cure sheets — the BEARER path (VIEWER-CONTRACTS.md §2b/§2c).
 *
 * Nothing here shares machinery with the device plane in §2: no HMAC, no serial,
 * no signing. Auth is the Entra bearer the app already sends.
 *
 * THE INTERCEPTOR ONLY SEES HttpClient. MSAL attaches the bearer through an
 * HttpInterceptor, so an `<a href>` or `window.open` to any of these endpoints
 * sends NO Authorization header and gets a 401 while looking perfectly correct in
 * the markup. That is why every PDF here is fetched as a BLOB through HttpClient
 * and opened from an object URL — it is the reason the stored SAS was removed
 * rather than repointed, not a style preference.
 */
@Injectable({ providedIn: 'root' })
export class CureSheetService {
  constructor(
    private http: HttpClient,
    private auth: UserAuthService
  ) {}

  /**
   * What this user may choose from — already trimmed to their customer's
   * exposure, approved versions only, one version per sheet.
   *
   * AN EMPTY `sheets` ARRAY IS A SUCCESSFUL ANSWER meaning "this customer has
   * exposed nothing yet". It is not an error and must not be rendered as one.
   */
  getAudience(customerId?: string): Observable<CureSheetAudience> {
    let params = new HttpParams()
      .set('tenantId', this.auth.getCurrentCustomerTenantId())
      .set('objectId', this.auth.getObjectId());
    if (customerId) {
      params = params.set('customerId', customerId);
    }
    return this.http.get<CureSheetAudience>(this.url('v1/cure-sheets/effective'), { params });
  }

  /** The bearer PDF for a sheet this user may see. Access re-derived per request. */
  getSheetPdf(sheetId: string, version: number): Observable<CureSheetPdf> {
    const params = new HttpParams().set('objectId', this.auth.getObjectId());
    return this.http
      .get(this.url(`v1/cure-sheets/${encodeURIComponent(sheetId)}/versions/${version}/content`), {
        params,
        responseType: 'blob',
        observe: 'response',
      })
      .pipe(map((res) => this.toPdf(res)));
  }

  /**
   * Resolve a finished project's `cureSheetRef` — §2c.
   *
   * Answers EVEN WHEN the version is withdrawn, rejected, draft or on an
   * archived sheet: withdrawal governs future field delivery, it does not unwrite
   * history. Resolving through the audience list instead would lose the record
   * precisely in the incident case, because that list is by construction the set
   * that excludes withdrawn versions.
   *
   * `projectName` is REQUIRED — the server refuses without it (400), because
   * authorization derives from the PROJECT, not the sheet: the caller must be
   * able to see that project AND the project must carry this exact ref. A v2 pin
   * does not unlock v3, and that ref match is what makes enumeration impossible.
   */
  getProvenance(
    projectName: string,
    sheetId: string,
    version: number
  ): Observable<CureSheetProvenance> {
    const params = this.provenanceParams(projectName);
    return this.http.get<CureSheetProvenance>(
      this.url(`v1/cure-sheets/provenance/${encodeURIComponent(sheetId)}/versions/${version}`),
      { params }
    );
  }

  /** The provenance PDF, carrying its standing in a header. */
  getProvenancePdf(
    projectName: string,
    sheetId: string,
    version: number
  ): Observable<CureSheetPdf> {
    const params = this.provenanceParams(projectName);
    return this.http
      .get(
        this.url(
          `v1/cure-sheets/provenance/${encodeURIComponent(sheetId)}/versions/${version}/pdf`
        ),
        { params, responseType: 'blob', observe: 'response' }
      )
      .pipe(map((res) => this.toPdf(res)));
  }

  /**
   * Open fetched PDF bytes.
   *
   * Takes a BLOB, never a URL, so there is no shape of this function that could
   * be pointed at an endpoint and bypass the interceptor.
   */
  openPdf(pdf: CureSheetPdf): void {
    const objectUrl = URL.createObjectURL(pdf.blob);
    window.open(objectUrl, '_blank');
    // Revoked on a delay: revoking immediately can race the viewer's own load.
    setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
  }

  private provenanceParams(projectName: string): HttpParams {
    // Sent even when empty so a missing name produces the server's deliberate
    // 400 rather than a silently different URL shape that 404s and looks like a
    // denial.
    return new HttpParams()
      .set('projectName', projectName ?? '')
      .set('objectId', this.auth.getObjectId());
  }

  private toPdf(res: HttpResponse<Blob>): CureSheetPdf {
    return {
      blob: res.body ?? new Blob([], { type: 'application/pdf' }),
      standingHeader: res.headers.get('X-Vericure-Cure-Sheet-Standing'),
    };
  }

  private url(path: string): string {
    return `${environment.apiUrl}${path}`;
  }
}
